/**
 * @fileoverview 3×3 sliding-puzzle state for the home icon “puzzle” animation.
 * @purpose Random legal moves only: orthogonal swaps with the empty cell, one tile per step.
 * @ownership Core package
 */

/** Tile ids 0–7 occupy eight slots; `null` is the empty cell (only one). */
export type PuzzleLayout = readonly (number | null)[];

/** Solved order: tiles 0–7 row-major, empty bottom-right. */
export const PUZZLE_SOLVED: PuzzleLayout = [0, 1, 2, 3, 4, 5, 6, 7, null];

export const PUZZLE_TILE_IDS = [0, 1, 2, 3, 4, 5, 6, 7] as const;

export type ApplyRandomLegalMoveOptions = {
  /**
   * Previous grid index of the hole (before the last move). If it is still a neighbor of the
   * current hole, it is excluded when at least one other neighbor exists so we do not instantly
   * reverse the last slide.
   */
  avoidSlot?: number | null;
  /**
   * Uniform draws in `[0, 1)` — used to pick among legal neighbors. Defaults to `Math.random`
   * (browser / Node). Unit tests should pass a seeded PRNG for deterministic runs.
   */
  random?: () => number;
};

/** Pick a random orthogonal neighbor of the hole and swap (one tile moves). */
export function applyRandomLegalMove(
  layout: (number | null)[],
  options?: ApplyRandomLegalMoveOptions,
): (number | null)[] {
  const emptyIndex = layout.indexOf(null);
  if (emptyIndex < 0) return layout;

  const row = Math.floor(emptyIndex / 3);
  const col = emptyIndex % 3;
  const neighbors: number[] = [];
  if (col > 0) {
    neighbors.push(emptyIndex - 1);
  }
  if (col < 2) {
    neighbors.push(emptyIndex + 1);
  }
  if (row > 0) {
    neighbors.push(emptyIndex - 3);
  }
  if (row < 2) {
    neighbors.push(emptyIndex + 3);
  }

  const avoid = options?.avoidSlot;
  let choices = neighbors;
  if (avoid != null && neighbors.includes(avoid) && neighbors.length > 1) {
    choices = neighbors.filter((n) => n !== avoid);
  }

  const rng = options?.random ?? Math.random;
  const pick = choices[Math.floor(rng() * choices.length)]!;
  const next = [...layout];
  [next[emptyIndex], next[pick]] = [next[pick], next[emptyIndex]];
  return next;
}

export type ShufflePuzzleOptions = Pick<ApplyRandomLegalMoveOptions, 'random'>;

/** Apply many random legal moves from `start` (keeps state solvable). */
export function shufflePuzzle(
  start: PuzzleLayout,
  moveCount: number,
  options?: ShufflePuzzleOptions,
): (number | null)[] {
  const moveOpts: ApplyRandomLegalMoveOptions | undefined =
    options?.random !== undefined ? { random: options.random } : undefined;
  let layout = [...start] as (number | null)[];
  for (let i = 0; i < moveCount; i++) {
    layout = applyRandomLegalMove(layout, moveOpts);
  }
  return layout;
}

/**
 * When exactly one tile (0–7) changes slot between layouts (one orthogonal slide into the hole),
 * return that tile and its from/to indices. Otherwise `null` (e.g. initial shuffle or multi-change).
 */
export function findSingleSlidingTile(
  before: readonly (number | null)[],
  after: readonly (number | null)[],
): { tileId: number; fromSlot: number; toSlot: number } | null {
  let found: { tileId: number; fromSlot: number; toSlot: number } | null = null;
  for (const t of PUZZLE_TILE_IDS) {
    const a = before.indexOf(t);
    const b = after.indexOf(t);
    if (a !== b) {
      if (found !== null) {
        return null;
      }
      found = { tileId: t, fromSlot: a, toSlot: b };
    }
  }
  return found;
}
